/**
 * The public start page (the web landing page) as stored by the API (`StartPageDocument`). Mirrors
 * `Models/StartPageDocuments.cs`; the API clears every field a block type does not use.
 */

/** Text per locale (`en-US`, `sv-SE`, …). See `pickText` for the fallback order. */
export type LocalizedText = Partial<Record<string, string>>;

export type StartPageBlockType =
  | 'hero'
  | 'text'
  | 'image'
  | 'textImage'
  | 'features'
  | 'signIn'
  | 'appLauncherEmbed';

export interface StartPageFeatureItem {
  icon?: string | null;
  title?: LocalizedText | null;
  body?: LocalizedText | null;
}

export interface StartPageBlock {
  id: string;
  type: StartPageBlockType;
  hidden?: boolean;
  /** When set, the block gets an anchor link with this label in the page header. */
  navLabel?: LocalizedText | null;
  eyebrow?: LocalizedText | null;
  title?: LocalizedText | null;
  /** A small Markdown subset, see `RichText`. */
  body?: LocalizedText | null;
  buttonLabel?: LocalizedText | null;
  /** An image in the API's start-page image store; wins over `imageUrl`. */
  imageId?: string | null;
  /** An external https image. */
  imageUrl?: string | null;
  imageAlt?: LocalizedText | null;
  caption?: LocalizedText | null;
  imagePosition?: 'left' | 'right' | null;
  /** Feature cards. None means QuickDial's built-in cards. */
  items?: StartPageFeatureItem[] | null;
  appCode?: string | null;
  embedId?: string | null;
}

export interface PublicStartPage {
  /** False while nothing is stored: the SPA shows its built-in default page. */
  isCustomized: boolean;
  blocks: StartPageBlock[];
  updatedAt: string | null;
}

export interface PlatformStartPage extends PublicStartPage {
  updatedBy: string | null;
}

export interface StartPageImageUpload {
  id: string;
  contentType: string;
  sizeBytes: number;
}

/** Locales a block can carry text in: the SPA's own. */
export const START_PAGE_LOCALES = ['en-US', 'sv-SE', 'nb-NO', 'da-DK', 'fi-FI'] as const;

/** Mirrors `StartPageService.MaxImageBytes`. */
export const START_PAGE_MAX_IMAGE_BYTES = 1024 * 1024;

export const START_PAGE_MAX_BLOCKS = 40;

export const START_PAGE_MAX_FEATURE_ITEMS = 12;
